import Analytics from '@withlocals/withlocals-analytics';
import {
  fetchAccountInterests,
  postAccountInterests,
} from 'flux/clients/accountInterest';
import { Observable } from 'rxjs';
import { WlWindow } from 'typings/globals/cordova';
import { now } from 'utils/TimeHelper';

import { ServiceProps } from './Service';

declare const window: WlWindow;

const diffInterests = (storedInterest, interests) =>
  Object.keys(interests)
    .filter((key) => storedInterest[key] !== interests[key])
    .reduce((acc, curr) => {
      acc[curr] = interests[curr];
      return acc;
    }, {});

export function AppAnalytics(props: ServiceProps) {
  const accountInterests$ = props.authState$
    .filter(
      (x) => !!x.authInfo && !!x.authInfo.token && !x.fromCache && !x.offline,
    )
    .distinctUntilChanged(
      (prev, curr) => prev.authInfo.accountId === curr.authInfo.accountId,
    )
    .switchMap(() => fetchAccountInterests())
    .shareReplay(1);

  // Sync permissions
  Observable.combineLatest(props.permissions$, accountInterests$)
    .map(([permission, storedAccountInterest]) => {
      const prefix = 'APP.perms.';

      const flattenPermission = {
        [`${prefix}camera`]: String(permission.camera),
        [`${prefix}gps.inUse`]: String(permission.location.inUse),
        [`${prefix}gps.always`]: String(permission.location.background),
        [`${prefix}push`]: String(permission.push),
      };

      return diffInterests(storedAccountInterest, flattenPermission);
    })
    .subscribe((changedPermissionInterests) =>
      postAccountInterests(changedPermissionInterests),
    );

  Observable.combineLatest(accountInterests$)
    .map(([storedAccountInterest]) => [
      storedAccountInterest,
      {
        platform: window.cordova?.platformId,
        device: window.device && window.device.manufacturer,
        devicemodel: window.device && window.device.model,
        deviceuuid: window.device && window.device.uuid,
      },
    ])
    .do(([, appData]) => Analytics.appOpen(appData))
    .map(([storedAccountInterest, appData]) => {
      const prefix = 'APP.';

      const flattenAppData = Object.keys(appData).reduce((acc, curr) => {
        acc[`${prefix}${curr}`] = appData[curr];
        return acc;
      }, {});

      return diffInterests(storedAccountInterest, flattenAppData);
    })
    .subscribe((changedAppData) => postAccountInterests(changedAppData));

  // Save firstOpen and lastOpen
  accountInterests$
    .map((storedAccountInterest) => {
      const date = now();
      const opensToSave = {
        'APP.lastopen': date,
      };
      if (!storedAccountInterest['APP.firstopen']) {
        opensToSave['APP.firstopen'] = date;
      }

      return opensToSave;
    })
    .subscribe((changedOpenData) => {
      postAccountInterests(changedOpenData);
      if (changedOpenData['APP.firstopen']) {
        Analytics.track('App', 'First open', window.cordova?.platformId);
      }
    });
}
